import React, { useMemo, useState, useEffect, useCallback } from "react";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Card, CardBody, Button, Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { generateColumns } from "../../../custom/Utils/ColumnUtils";
import useFetchData from "../../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../../custom/Utils/UsePaginationUtils";
import useListFilters from "../../../custom/Utils/UseListFiltersUtils";
import { listPageData, exportPageData } from "./ItemFile";
import useDeleteRecord from "../../../custom/Utils/UseDeleteRecordUtils.js";
import DeleteConfirmModal from "../../../custom/components/Modal/DeleteConfirmModal.js";
import { getRecords } from "../../../services/api.service";
import SmartImportButton from "../../../custom/components/Import/SmartImportButton.js";

const ItemList = () => {
  const navigate = useNavigate();

  /* ---------------- STATE ---------------- */
  const [updatedData, setUpdatedData] = useState([]);
  const [dataForExport, setDataForExport] = useState([]);

  const {
    payload,
    navigatePage,
    headerName,
    columnData,
    endpoint,
    searchfields,
    exportFileName,
    filterConfig,
  } = listPageData();

  const initialPayload = useMemo(() => payload, []);

  /* ---------------- PAGINATION ---------------- */
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
  } = usePagination();

  // list filters (config comes from the master's *File.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  const { data, isLoading, setSearchKeyword, totalCount, refetch } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage }
  );

  useEffect(() => {
    if (data) {
      const newArray = data.map((item) => {
        return {
          ...item,
          category_type:
            item?.category?.type === null || item?.category?.type === undefined
              ? "-"
              : item.category?.type === 0
              ? "Material"
              : "Sparepart",
        };
      });
      setUpdatedData(newArray);
    }
  }, [data]);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  // handle edit action
  const handleEdit = useCallback(
    (id) => {
      navigate(navigatePage.edit + "/" + id);
    },
    [navigate, navigatePage.edit]
  );

  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view]
  );

  // delete flow: confirmation popup + server side dependency check
  const deleteAction = useDeleteRecord({
    modelName: payload.modelName,
    label: headerName,
    onDeleted: refetch,
  });

  // columns data
  const columns = useMemo(
    () =>
      generateColumns(
        updatedData,
        columnData,
        handleEdit,
        handleView,
        page,
        pageSize,
        null,
        null,
        { onDelete: deleteAction.requestDelete }
      ),
    [updatedData, columnData, handleEdit, handleView, page, pageSize, deleteAction.requestDelete]
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount]
  );

  /* ---------------- PAGE META ---------------- */

  /* ---------------- EXPORT ---------------- */
  useEffect(() => {
    const fetchExport = async () => {
      const res = await getRecords(exportPageData, endpoint);
      setDataForExport(res?.data || []);
    };
    fetchExport();
  }, []);

  /* ---------------- EXPORT MAP ---------------- */
  const exportData = dataForExport.map((item, index) => ({
    "Sr. No": index + 1,
    "GMDS CODE": item?.gmds_code || "-",
    "ITEM DESCRIPTION": item?.descriptions || "-",
    "CATEGORY NAME": item?.category?.name || "-",
    "UOM NAME": item?.uom?.name || "-",
    "OEM NAME": item?.oem?.name || "-",
    "GROUP NAME": item?.group?.name || "-",
    "PRIORITY 1 ENGINEER": item?.priorityOneUser?.full_name || "-",
    "PRIORITY 2 ENGINEER": item?.priorityTwoUser?.full_name || "-",
    "PRIORITY 3 ENGINEER": item?.priorityThreeUser?.full_name || "-",
    STATUS: item?.is_active ? "Active" : "Inactive",
  }));

  /* ---------------- RENDER ---------------- */
  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={`${headerName} List`} breadcrumbItems={[]} />

            <Button
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button>

            <SmartImportButton
              module="item"
              label={headerName}
              onImported={refetch}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={updatedData || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>

          <DeleteConfirmModal {...deleteAction.modalProps} />
        </Container>
      </div>
    </>
  );
};

export default ItemList;
